import { ColDef, GetRowIdParams, ValueFormatterParams, ValueGetterParams } from '@ag-grid-community/core';
import { Injectable } from '@angular/core';
import type { GetPcapPlayerFilesQuery } from '@uigy/generated/graphql';
import { DatesFormatsEnum } from '@uigy/shared/enums/dateFormats.enum';
import { GridNamesEnum } from '@uigy/shared/enums/gridNames.enum';
import { GetToolPanelSettings } from '@uigy/shared/modules/grid/grids/components/generic-basic-grid/services/defaultBasicGridSettings.constant';
import { GridCustomFiltersEnum, GridSettings } from '@uigy/shared/modules/grid/models/grid';
import { FileSizesPipe } from '@uigy/shared/pipes/file-sizes.pipe';
import { DateTimeService } from '@uigy/shared/services/date-time/date-time.service';
import { LanguageService } from '@uigy/shared/services/language.service';
import Utils from '@uigy/utils';
import { GridFormatterService } from '@uigy/shared/modules/grid/formatters/grid-formatter.service';
import { createFilterComponentMap } from '@uigy/shared/modules/grid/filters/time-filter/time-filter-selector.interfaces';

@Injectable({
  providedIn: 'root',
})
export class PcapPlayerGridSettingsService {
  private readonly TEXTS = this.languageService.texts.settings.env_config.pcap_player.column_defs;
  private readonly NEVER_TEXT = this.languageService.texts.settings.env_config.pcap_player.play.never;
  private readonly NAME: ColDef = {
    headerName: this.TEXTS.name,
    field: 'name',
    minWidth: 250,
    enableRowGroup: false,
  };
  private readonly SIZE: ColDef = {
    headerName: this.TEXTS.size,
    field: 'size',
    minWidth: 100,
    valueFormatter(param: ValueFormatterParams): string {
      return (param.value != null && GridFormatterService.fileSizeFormatter(param.value)) || '';
    },
    enableRowGroup: false,
    filterParams: {
      valueFormatter(param: ValueFormatterParams): string {
        return (param.value != null && GridFormatterService.fileSizeFormatter(param.value)) || '';
      },
    },
  };
  private readonly UPLOAD_AT: ColDef = {
    headerName: this.TEXTS.upload_at,
    field: 'uploadedAt',
    filter: GridCustomFiltersEnum.timeFilterSelectorComponent,
    minWidth: 200,
    comparator: Utils.sortByDate,
    valueGetter: (params: ValueGetterParams) => {
      return params.data.uploadedAt ? DateTimeService.utcToLocalFormatted(params.data.uploadedAt, DatesFormatsEnum.general) : this.NEVER_TEXT;
    },
    enableRowGroup: false,
    filterParams: {
      componentMap: createFilterComponentMap(),
    }
  };
  private readonly UPLOAD_BY: ColDef = {
    headerName: this.TEXTS.uploaded_by,
    field: 'uploadedBy',
    minWidth: 200,
  };
  private readonly LAST_PLAYED: ColDef = {
    headerName: this.TEXTS.last_played,
    filter: GridCustomFiltersEnum.timeFilterSelectorComponent,
    field: 'lastPlayed',
    comparator: Utils.sortByDate,
    sort: 'desc',
    minWidth: 200,
    valueFormatter: (params: ValueFormatterParams) => {
      return params.data?.lastPlayed ? DateTimeService.utcToLocalFormatted(params.data.lastPlayed, DatesFormatsEnum.general) : this.NEVER_TEXT;
    },
    enableRowGroup: false,
    filterParams: {
      componentMap: createFilterComponentMap(),
    }
  };
  private readonly PLAYED_BY: ColDef = {
    headerName: this.TEXTS.played_by,
    field: 'playedBy',
    valueFormatter: params => params.value || this.NEVER_TEXT,
    minWidth: 200,
  };

  constructor(private languageService: LanguageService) {}

  public gridSettings(): GridSettings<GetPcapPlayerFilesQuery['pcapPlayer']['files']['nodes'][number]> {
    return {
      deactivateMode: {
        preserveGridState: true,
        gridName: GridNamesEnum.pcapPlayer,
      },
      gridOptions: {
        columnDefs: [this.NAME, this.SIZE, this.UPLOAD_AT, this.UPLOAD_BY, this.LAST_PLAYED, this.PLAYED_BY],
        suppressContextMenu: false,
        groupDefaultExpanded: -1,
        getRowId: (row: GetRowIdParams<GetPcapPlayerFilesQuery['pcapPlayer']['files']['nodes'][number]>): string => row.data.id,
        sideBar: {
          toolPanels: GetToolPanelSettings(false, false),
        },
      },
    };
  }
}
